<script setup>
import LoadingSpinner from '@js/components/LoadingSpinner.vue';
import DialogDrawer from '@js/components/dialogs/DialogDrawer.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError } from '@js/utils/toast';
import { reactive, ref, watch } from 'vue';
import { array, mixed, object, string } from 'yup';

const { ticket, callback } = defineProps({
    ticket: {
        type: Object,
        required: true,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const themes = ref(null);

const priorities = ref(null);

const statuses = ref(null);

const tenantUsers = ref(null);

const users = ref(null);

const loading = ref(true);

const isOpen = ref(false);

const isSubmitting = ref(false);

const form = reactive({
    theme: null,
    name: ticket.name,
    isBillable: ticket.isBillable ?? false,
    priority: null,
    status: null,
    linkedTenantUsers: [],
    users: [],
});

const validationSchema = object({
    theme: object().nullable(),
    isBillable: mixed().nullable(),
    name: string().required('Le nom est requis'),
    priority: object().required('La priorité est requise'),
    status: object().required('Le statut est requis'),
    linkedTenantUsers: array().of(object()).nullable(),
    users: array().of(object()).nullable(),
});

function updateData() {
    loading.value = true;

    Promise.all([
        axios.get(route('front.projectManagement.task.getTicketThemes')),
        axios.get(route('front.projectManagement.task.getTicketPriorities')),
        axios.get(route('front.projectManagement.task.getTicketStatuses')),
        axios.get(route('front.projectManagement.task.getTicketApikomUsers')),
        axios.get(
            route('front.getTenantUsers', {
                tenant: ticket.tenant.id,
            }),
        ),
    ])
        .then((response) => {
            themes.value = response[0].data;
            priorities.value = response[1].data;
            statuses.value = response[2].data;
            users.value = response[3].data;
            tenantUsers.value = response[4].data;

            if (ticket.theme) {
                form.theme = themes.value.find(
                    (theme) => theme.value === ticket.theme.value,
                );
            }

            if (
                ticket.linkedTenantUsers &&
                ticket.linkedTenantUsers.length > 0
            ) {
                form.linkedTenantUsers = tenantUsers.value.filter(
                    (tenantUser) =>
                        ticket.linkedTenantUsers.some(
                            (linkedTenantUser) =>
                                linkedTenantUser.id === tenantUser.id,
                        ),
                );
            }

            form.priority = priorities.value.find(
                (priority) => priority.value === ticket.priority.value,
            );

            form.status = statuses.value.find(
                (status) => status.value === ticket.status.value,
            );

            form.users = users.value.filter((user) =>
                ticket.users.some((ticketUser) => ticketUser.id === user.id),
            );
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            loading.value = false;
        });
}

function submit() {
    isSubmitting.value = true;

    axios
        .patch(
            route('front.projectManagement.task.patchTicket', {
                ticket: ticket.id,
            }),
            {
                name: form.name,
                priority: form.priority.value,
                status: form.status.value,
                theme: form.theme ? form.theme.value : null,
                isBillable: form.isBillable,
                linkedTenantUserIds:
                    form.linkedTenantUsers && form.linkedTenantUsers.length > 0
                        ? form.linkedTenantUsers.map((user) => user.id)
                        : null,
                userIds:
                    form.users && form.users.length > 0
                        ? form.users.map((user) => user.id)
                        : null,
            },
        )
        .then(() => {
            isOpen.value = false;

            if (callback) {
                callback();
            }
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);
        });
}

watch(isOpen, (value) => {
    if (value) {
        updateData();
    }
});
</script>

<template>
    <DialogDrawer v-model="isOpen" size="extra-large">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title> Mise à jour du ticket #{{ ticket.id }} </template>

        <template #full-content>
            <div v-if="loading" class="px-6 py-4">
                <LoadingSpinner />
            </div>

            <FormRoot
                v-else
                :validation-schema="validationSchema"
                :submit="submit"
                v-slot="{ errors }"
            >
                <FormColumn>
                    <FormContent>
                        <ListboxField
                            v-model="form.theme"
                            field-id="theme"
                            field-name="theme"
                            label="Thème"
                            :options="themes"
                            has-empty-option
                        >
                            <template #buttonContent>
                                {{ form.theme.label }}
                            </template>

                            <template #optionContent="{ option }">
                                {{ option.label }}
                            </template>
                        </ListboxField>

                        <InputField
                            v-model="form.name"
                            label="Nom"
                            field-name="name"
                            field-id="name"
                            required
                        />

                        <SwitchField
                            v-model="form.isBillable"
                            field-id="isBillable"
                            field-name="isBillable"
                            label="Facturable"
                        />
                    </FormContent>

                    <FormContent>
                        <ComboboxField
                            v-if="tenantUsers"
                            v-model="form.linkedTenantUsers"
                            :options="tenantUsers"
                            label="Utilisateurs clients liés"
                            field-name="linkedTenantUsers"
                            field-id="linkedTenantUsers"
                            filteredOptionKey="fullName"
                            has-empty-option
                            multiple
                        >
                            <template #optionContent="{ option }">
                                {{ option.fullName }} - {{ option.email }}
                            </template>
                        </ComboboxField>

                        <ComboboxField
                            v-model="form.users"
                            :options="users"
                            label="Responsable(s)"
                            field-name="users"
                            field-id="users"
                            filteredOptionKey="fullName"
                            has-empty-option
                            multiple
                        >
                            <template #optionContent="{ option }">
                                {{ option.fullName }}
                            </template>
                        </ComboboxField>
                    </FormContent>

                    <FormContent>
                        <ListboxField
                            v-model="form.priority"
                            field-id="priority"
                            field-name="priority"
                            label="Priorité"
                            :options="priorities"
                            required
                        >
                            <template #buttonContent>
                                {{ form.priority.label }}
                            </template>

                            <template #optionContent="{ option }">
                                {{ option.label }}
                            </template>
                        </ListboxField>

                        <ListboxField
                            v-model="form.status"
                            field-id="status"
                            field-name="status"
                            label="Statut"
                            :options="statuses"
                            required
                        >
                            <template #buttonContent>
                                {{ form.status.label }}
                            </template>

                            <template #optionContent="{ option }">
                                {{ option.label }}
                            </template>
                        </ListboxField>
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :is-submitting="isSubmitting"
                    :errors="errors"
                    without-back-button
                />
            </FormRoot>
        </template>
    </DialogDrawer>
</template>
